import React, { useState } from 'react';
import { Upload, Music, Check, AlertCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const genresList = ['Reggaeton', 'Bachata', 'Salsa', 'Merengue', 'Dembow', 'Hip-Hop', 'House', 'Afrobeat', 'Cumbia', 'Urbano Latino', 'Trap Latino', 'Guaracha'];

const Submit = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    artist: '',
    songTitle: '',
    featuring: '',
    genre: '',
    email: '',
    bpm: '',
    key: '',
    notes: '',
    file: null,
  });
  const [submitted, setSubmitted] = useState(false);

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.artist || !form.songTitle || !form.genre || !form.email) {
      toast({ title: 'Missing fields', description: 'Please fill in artist, song title, genre, and email.' });
      return;
    }
    // Save to localStorage as mock backend
    const existing = JSON.parse(localStorage.getItem('gcl_submissions') || '[]');
    const record = { ...form, file: form.file?.name || null, id: Date.now(), status: 'PENDING REVIEW' };
    localStorage.setItem('gcl_submissions', JSON.stringify([record, ...existing]));
    setSubmitted(true);
    toast({ title: 'Submission received', description: 'Your track is now in the DJ review queue.' });
  };

  if (submitted) {
    return (
      <div className="px-6 py-16 max-w-2xl mx-auto">
        <div className="rounded-2xl border border-orange-500/25 bg-gradient-to-br from-orange-500/10 to-transparent p-10 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-orange-500 flex items-center justify-center">
            <Check className="w-8 h-8 text-black" />
          </div>
          <h2 className="font-display text-3xl text-white mt-6">Track Submitted</h2>
          <p className="text-neutral-400 mt-2">
            Your submission is now in the queue. Gulf Coast DJs review new music every 48 hours. If accepted, your track will appear in the record pool.
          </p>
          <button
            onClick={() => { setSubmitted(false); setForm({ artist: '', songTitle: '', featuring: '', genre: '', email: '', bpm: '', key: '', notes: '', file: null }); }}
            className="mt-8 px-6 py-2.5 rounded-full bg-white text-black text-sm font-semibold hover:bg-neutral-200 transition-colors"
          >
            Submit Another Track
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 py-10 max-w-3xl mx-auto">
      <div className="flex items-center gap-2 text-orange-400 text-xs font-semibold uppercase tracking-widest">
        <Music className="w-4 h-4" /> For Artists
      </div>
      <h1 className="font-display text-4xl md:text-5xl text-white mt-3">Submit Music To Gulf Coast DJs</h1>
      <p className="text-neutral-400 mt-3 max-w-2xl">
        Upload your unreleased track directly to our DJ crate. Verified DJs across Tampa, Miami, Houston, and New Orleans review every submission and add the best cuts to their sets and the record pool.
      </p>

      <div className="mt-6 flex items-center gap-2 text-xs text-neutral-500 bg-white/[0.03] border border-white/5 rounded-lg p-3">
        <AlertCircle className="w-4 h-4 text-orange-400 flex-shrink-0" />
        Mastered WAV / MP3 320kbps. Include clean version if available. Non-exclusive rights.
      </div>

      <form onSubmit={onSubmit} className="mt-8 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Artist Name *">
            <input
              value={form.artist}
              onChange={(e) => onChange('artist', e.target.value)}
              placeholder="e.g. Lupe Sol"
              className="gcl-input"
            />
          </Field>
          <Field label="Song Title *">
            <input
              value={form.songTitle}
              onChange={(e) => onChange('songTitle', e.target.value)}
              placeholder="e.g. Miami Anthem"
              className="gcl-input"
            />
          </Field>
          <Field label="Featuring">
            <input
              value={form.featuring}
              onChange={(e) => onChange('featuring', e.target.value)}
              placeholder="Optional collaborators"
              className="gcl-input"
            />
          </Field>
          <Field label="Genre *">
            <select
              value={form.genre}
              onChange={(e) => onChange('genre', e.target.value)}
              className="gcl-input"
            >
              <option value="">Select genre</option>
              {genresList.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </Field>
          <Field label="BPM">
            <input
              value={form.bpm}
              onChange={(e) => onChange('bpm', e.target.value)}
              placeholder="e.g. 92"
              className="gcl-input"
            />
          </Field>
          <Field label="Key">
            <input
              value={form.key}
              onChange={(e) => onChange('key', e.target.value)}
              placeholder="e.g. C minor"
              className="gcl-input"
            />
          </Field>
          <Field label="Contact Email *" className="md:col-span-2">
            <input
              type="email"
              value={form.email}
              onChange={(e) => onChange('email', e.target.value)}
              placeholder="you@label.com"
              className="gcl-input"
            />
          </Field>
          <Field label="Notes for DJs" className="md:col-span-2">
            <textarea
              value={form.notes}
              onChange={(e) => onChange('notes', e.target.value)}
              placeholder="Anything the DJs should know about your track"
              rows={3}
              className="gcl-input resize-none"
            />
          </Field>
        </div>

        <div>
          <label className="block text-xs text-neutral-400 uppercase tracking-wider mb-2">Audio File</label>
          <label className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-white/10 hover:border-orange-500/50 rounded-xl p-8 cursor-pointer transition-colors bg-white/[0.02]">
            <div className="w-12 h-12 rounded-full bg-orange-500/15 flex items-center justify-center">
              <Upload className="w-5 h-5 text-orange-400" />
            </div>
            <div className="text-center">
              <div className="text-sm text-white">{form.file ? form.file.name : 'Drop your track or click to browse'}</div>
              <div className="text-xs text-neutral-500 mt-1">WAV / MP3 320 — Max 50MB</div>
            </div>
            <input
              type="file"
              accept="audio/*"
              className="hidden"
              onChange={(e) => onChange('file', e.target.files?.[0] || null)}
            />
          </label>
        </div>

        <div className="pt-2">
          <button
            type="submit"
            className="w-full md:w-auto px-8 py-3 rounded-full bg-orange-500 hover:bg-orange-400 text-black font-semibold text-sm transition-colors"
          >
            Send To DJ Crate
          </button>
          <p className="text-[11px] text-neutral-500 mt-3">
            By submitting you confirm you own or represent the rights to this recording.
          </p>
        </div>
      </form>
    </div>
  );
};

const Field = ({ label, children, className = '' }) => (
  <div className={className}>
    <label className="block text-xs text-neutral-400 uppercase tracking-wider mb-2">{label}</label>
    {children}
  </div>
);

export default Submit;
